import type { CppRateCard } from '~/services/LuxLoyalty';

/** The minimum shape needed to format a rate card's CPP for display. */
type CppFormattable = Pick<CppRateCard, 'cppMinCents' | 'cppMaxCents' | 'pricingModel'>;

/**
 * Formats a single CPP value. CPP is in cents, so values are suffixed with `c`; fractional cents
 * such as `0.5c` are valid and deliberately preserved rather than rounded to a fixed precision.
 *
 * The one place a CPP is turned into text, so rate cards, the transactions grid, the activity
 * statement and the invoice cannot drift apart on how a rate reads.
 *
 * @param cpp - The rate in cents.
 * @returns The formatted rate, e.g. `"1.2c"`.
 */
export function formatCppCents(cpp: number): string {
  return `${cpp}c`;
}

/**
 * Formats a rate card's CPP for display: a single value for a FLAT card (or a degenerate
 * TIERED card whose min and max happen to match), otherwise a min–max range.
 *
 * @param card - The rate card (or any object carrying its cpp min/max cents and pricing model).
 * @returns The formatted CPP string, e.g. `"5c"` or `"5c - 8c"`.
 */
export function formatCpp(card: CppFormattable): string {
  if (card.pricingModel === 'FLAT' || card.cppMinCents === card.cppMaxCents) {
    return `${card.cppMinCents}c`;
  }
  return `${card.cppMinCents}c - ${card.cppMaxCents}c`;
}
